import {
    createStickyNote,
    deleteStickyNote,
    fetchStickyNotes,
    updateStickyNote,
} from '../../context/CommonContex';

/**
 * Sticky notes store
 * ------------------------------------------------------------------
 * Talks to /api/stickyNotes (see docs/sticky-notes-and-comment-chats.md).
 *
 * Until that endpoint is deployed the API answers 404, and the store switches to
 * "local" mode: notes are kept in this browser's localStorage so the feature can be
 * used and reviewed, and the board says plainly that they are not shared yet.
 * Any other error (offline, 500, 401) is reported, never silently stored locally.
 */

export const STICKY_NOTE_COLORS = [
    { id: 'yellow', label: 'Yellow' },
    { id: 'orange', label: 'Orange' },
    { id: 'blue', label: 'Blue' },
    { id: 'green', label: 'Green' },
    { id: 'pink', label: 'Pink' },
    { id: 'periwinkle', label: 'Periwinkle' },
];
export const STICKY_NOTE_MAX_LENGTH = 500;

const COLOR_IDS = new Set([...STICKY_NOTE_COLORS.map((c) => c.id), 'purple']);
const LOCAL_KEY = 'bridge.stickyNotes.v1';
const BATCH_SIZE = 100;

let mode = 'unknown'; // 'unknown' | 'remote' | 'local'
const modeListeners = new Set();

export const getStickyNotesMode = () => mode;

export const subscribeStickyNotesMode = (fn) => {
    modeListeners.add(fn);
    return () => modeListeners.delete(fn);
};

const setMode = (next) => {
    if (mode === next) return;
    mode = next;
    modeListeners.forEach((fn) => fn(next));
};

const isMissingEndpoint = (err) => {
    const status = err?.response?.status;
    return status === 404 || status === 405 || status === 501;
};

const isLocalId = (id) => String(id || '').startsWith('local-');

const currentUser = () => (
    localStorage.getItem('userEmail')
    || localStorage.getItem('email')
    || localStorage.getItem('userName')
    || ''
);

const toTime = (value) => {
    const t = new Date(value || 0).getTime();
    return Number.isFinite(t) ? t : 0;
};

export function normalizeStickyNote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id ?? raw.noteId ?? raw.stickyNoteId ?? raw._id;
    if (id == null || id === '') return null;
    const color = String(raw.color || '').toLowerCase();
    return {
        id: String(id),
        orderNumber: String(raw.orderNumber ?? raw.ordernum ?? raw.order_number ?? ''),
        orderLineId: raw.orderLineId ?? raw.order_line_id ?? null,
        text: String(raw.text ?? raw.note ?? raw.noteText ?? raw.content ?? ''),
        color: COLOR_IDS.has(color) ? color : 'yellow',
        createdBy: raw.createdBy ?? raw.created_by ?? raw.addedBy ?? '',
        createdAt: raw.createdAt ?? raw.created_at ?? null,
        updatedBy: raw.updatedBy ?? raw.updated_by ?? null,
        updatedAt: raw.updatedAt ?? raw.updated_at ?? null,
    };
}

/** Newest note first: it is the one shown on top of the stack in the grid. */
export function sortStickyNotes(notes) {
    return [...(notes || [])].sort((a, b) => toTime(b.createdAt) - toTime(a.createdAt));
}

const readLocal = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (err) {
        return {};
    }
};

const writeLocal = (map) => {
    try {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(map));
    } catch (err) {
        throw new Error('This browser could not store the sticky note.');
    }
};

const updateLocal = (orderNumber, updater) => {
    const map = readLocal();
    const key = String(orderNumber);
    map[key] = updater(map[key] || []);
    if (!map[key].length) delete map[key];
    writeLocal(map);
};

const groupByOrder = (notes, orderNumbers) => {
    const out = {};
    orderNumbers.forEach((n) => { out[n] = []; });
    notes.forEach((note) => {
        if (!out[note.orderNumber]) out[note.orderNumber] = [];
        out[note.orderNumber].push(note);
    });
    Object.keys(out).forEach((n) => { out[n] = sortStickyNotes(out[n]); });
    return out;
};

/** Notes for many orders at once: `{ [orderNumber]: notes newest-first }`. */
export async function loadStickyNotes(orderNumbers) {
    const list = Array.from(new Set((orderNumbers || []).filter(Boolean).map(String)));
    if (!list.length) return {};

    if (mode !== 'local') {
        try {
            const notes = [];
            for (let i = 0; i < list.length; i += BATCH_SIZE) {
                const data = await fetchStickyNotes(list.slice(i, i + BATCH_SIZE));
                if (!Array.isArray(data)) {
                    // Anything but a list (for example an HTML fallback page) means the
                    // endpoint is not really there yet.
                    const notDeployed = new Error('Sticky notes endpoint is not deployed');
                    notDeployed.response = { status: 404 };
                    throw notDeployed;
                }
                notes.push(...data.map(normalizeStickyNote).filter(Boolean));
            }
            setMode('remote');
            return groupByOrder(notes, list);
        } catch (err) {
            if (!isMissingEndpoint(err)) throw err;
            setMode('local');
        }
    }

    const local = readLocal();
    const notes = list.flatMap((n) => (local[n] || []).map(normalizeStickyNote).filter(Boolean));
    return groupByOrder(notes, list);
}

export async function addStickyNote({ orderNumber, orderLineId, text, color }) {
    const payload = {
        orderNumber: String(orderNumber),
        orderLineId: orderLineId ?? null,
        text,
        color,
        createdBy: currentUser(),
    };

    if (mode !== 'local') {
        try {
            const saved = normalizeStickyNote(await createStickyNote(payload));
            setMode('remote');
            if (saved) return { ...saved, orderNumber: saved.orderNumber || payload.orderNumber };
            // The server saved it but sent nothing back: read it back so it has a real id.
            const reloaded = await loadStickyNotes([payload.orderNumber]);
            const match = (reloaded[payload.orderNumber] || []).find((n) => n.text === text);
            if (match) return match;
            throw new Error('The note was saved but could not be read back. Refresh the page to see it.');
        } catch (err) {
            if (!isMissingEndpoint(err)) throw err;
            setMode('local');
        }
    }

    const note = {
        ...payload,
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: new Date().toISOString(),
    };
    updateLocal(payload.orderNumber, (notes) => [note, ...notes]);
    return normalizeStickyNote(note);
}

export async function editStickyNote(note, { text, color }) {
    const changes = { text, color, updatedBy: currentUser() };

    if (mode !== 'local' && !isLocalId(note.id)) {
        try {
            const saved = normalizeStickyNote(await updateStickyNote(note.id, changes));
            setMode('remote');
            return saved
                ? { ...note, ...saved, orderNumber: saved.orderNumber || note.orderNumber }
                : { ...note, ...changes, updatedAt: new Date().toISOString() };
        } catch (err) {
            if (!isMissingEndpoint(err)) throw err;
            setMode('local');
        }
    }

    const next = { ...note, ...changes, updatedAt: new Date().toISOString() };
    updateLocal(note.orderNumber, (notes) => notes.map((n) => (String(n.id) === String(note.id) ? { ...n, ...next } : n)));
    return next;
}

export async function removeStickyNote(note) {
    if (mode !== 'local' && !isLocalId(note.id)) {
        try {
            await deleteStickyNote(note.id);
            setMode('remote');
            return;
        } catch (err) {
            if (!isMissingEndpoint(err)) throw err;
            setMode('local');
        }
    }
    updateLocal(note.orderNumber, (notes) => notes.filter((n) => String(n.id) !== String(note.id)));
}
